import { RefObject, useCallback, useEffect, useId, useRef, useState } from 'react';

/** Where a hover card's anchor sits in the viewport. A cursor point is just a rect with no height. */
export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
}

/** Fired on `document` when a card opens, carrying the id of the card that opened. */
const CARD_OPENED = 'hs:hover-card-opened';

/** How a card was opened. Scrolling means different things for a cursor and for the keyboard. */
type OpenSource = 'pointer' | 'focus';

export const sameAnchorRect = (a: AnchorRect | null, b: AnchorRect) =>
  a !== null && a.top === b.top && a.bottom === b.bottom && a.left === b.left;

export const readAnchorRect = (element: Element): AnchorRect => {
  const { top, bottom, left } = element.getBoundingClientRect();
  return { top, bottom, left };
};

/**
 * Makes a hover card close when any other one opens, and returns the announcement its own opening has to
 * make.
 *
 * Hover cards cannot rely on being told when to close. A mouse leaving an element is reported as a
 * `mouseout`/`mouseover` pair, and when the element moves out from under a stationary cursor - scrolling a
 * list with the wheel - the browser does not reliably send both halves, so the leave is never derived. Every
 * row the cursor passes then keeps its card and the screen fills with them.
 *
 * So closing is not left to the leave alone: at most one card exists at a time, which is an invariant that
 * holds no matter which events went missing. `close` must be stable.
 */
export function useExclusiveHoverCard(open: boolean, close: () => void) {
  const id = useId();

  useEffect(() => {
    if (!open) return;

    const onOtherCardOpened = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== id) close();
    };

    document.addEventListener(CARD_OPENED, onOtherCardOpened);
    return () => document.removeEventListener(CARD_OPENED, onOtherCardOpened);
  }, [open, id, close]);

  return useCallback(() => document.dispatchEvent(new CustomEvent(CARD_OPENED, { detail: id })), [id]);
}

/**
 * Tracks an element's viewport rect while its hover card is open, so a fixed-positioned card stays stuck to
 * it. Open state is the rect being non-null.
 */
export function useAnchorRect<T extends HTMLElement>(ref: RefObject<T | null>) {
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const source = useRef<OpenSource>('pointer');

  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return;

    const rect = readAnchorRect(element);
    setAnchor((previous) => (sameAnchorRect(previous, rect) ? previous : rect));
  }, [ref]);

  const clear = useCallback(() => setAnchor(null), []);
  const open = anchor !== null;
  const announceOpen = useExclusiveHoverCard(open, clear);

  const openFor = useCallback(
    (openedBy: OpenSource) => {
      source.current = openedBy;
      announceOpen();
      measure();
    },
    [announceOpen, measure]
  );

  const openByPointer = useCallback(() => openFor('pointer'), [openFor]);
  const openByFocus = useCallback(() => openFor('focus'), [openFor]);

  useEffect(() => {
    if (!open) return;

    // A card the cursor opened closes on scroll. The hover that opened it is no longer trustworthy once the
    // page moves - the row it belongs to may be nowhere near the cursor any more - and a card the reader
    // cannot dismiss by moving away is worse than one that needs a nudge to come back.
    //
    // A card the keyboard opened stays: focus is unambiguous, so it only needs re-measuring to keep up with
    // the element it is attached to.
    const onScroll = () => (source.current === 'pointer' ? clear() : measure());

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, clear, measure]);

  return { anchor, openByPointer, openByFocus, clear };
}
